import { useEffect, useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import { Loader2, BadgeCheck, Compass, Sparkles, Search, Megaphone, ChevronLeft, ChevronRight, Award } from "lucide-react";
import api, { mediaUrl, formatCount } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppNav from "@/components/AppNav";
import PostCard from "@/components/PostCard";
import { SupporterBadge } from "@/lib/badges";
import { Input } from "@/components/ui/input";

function CategoryChips({ categories, category, setCategory }) {
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [categories]);

  const scrollBy = (delta) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: "smooth" });
  };

  const items = ["Tutte", ...categories];

  return (
    <div className="relative -mx-5 sm:-mx-8 mb-6" data-testid="category-filter-bar">
      {canLeft && (
        <>
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-14 z-10 bg-gradient-to-r from-[#0D0D0D] to-transparent" />
          <button
            type="button" onClick={() => scrollBy(-220)} data-testid="category-scroll-left"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-[#1A1A1A] border border-white/15 flex items-center justify-center text-white/80 hover:border-[#E91E8C]/60 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        </>
      )}
      {canRight && (
        <>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-14 z-10 bg-gradient-to-l from-[#0D0D0D] to-transparent" />
          <button
            type="button" onClick={() => scrollBy(220)} data-testid="category-scroll-right"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-8 h-8 rounded-full bg-[#1A1A1A] border border-white/15 flex items-center justify-center text-white/80 hover:border-[#E91E8C]/60 hover:text-white transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </>
      )}
      <div ref={scrollRef} onScroll={update} className="overflow-x-auto no-scrollbar px-5 sm:px-8">
        <div className="flex gap-2 min-w-max pb-1">
          {items.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              data-testid={`category-chip-${cat}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border transition-colors ${
                category === cat
                  ? "bg-[#E91E8C] border-[#E91E8C] text-white"
                  : "bg-transparent border-white/15 text-white/70 hover:border-[#E91E8C]/50 hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}


function SponsoredFeedCard({ sponsor }) {
  return (
    <Link
      to={`/creator/${sponsor.creator_id}`}
      data-testid={`feed-sponsored-${sponsor.creator_id}`}
      className="block bg-[#1A1A1A] border border-[#F5C6D6]/30 rounded-3xl p-5 card-hover-glow"
    >
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="w-3.5 h-3.5 text-[#F5C6D6]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#F5C6D6]">Sponsorizzato</span>
        <span className="ml-auto text-[9px] font-bold uppercase tracking-wider bg-[#F5C6D6] text-black px-2 py-0.5 rounded-full">ADV</span>
      </div>
      <div className="flex items-center gap-4">
        <img src={mediaUrl(sponsor.avatar_url)} alt={sponsor.display_name} className="w-14 h-14 rounded-full object-cover border border-white/10" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-bold font-heading truncate">{sponsor.display_name}</span>
            {sponsor.is_verified && <BadgeCheck className="w-4 h-4 text-[#F5C6D6] shrink-0" />}
          </div>
          <p className="text-xs text-[#E91E8C] font-semibold mt-0.5">
            {sponsor.profile_type === "subscription" ? `€${Number(sponsor.subscription_price).toFixed(2)}/mese` : "Profilo gratuito"}
          </p>
        </div>
        <span className="shrink-0 text-xs font-semibold bg-[#E91E8C] text-white px-4 py-2 rounded-full">Vedi profilo</span>
      </div>
    </Link>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [creators, setCreators] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("Tutte");
  const [categories, setCategories] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [sponsoredPosts, setSponsoredPosts] = useState([]);
  const [fanBadges, setFanBadges] = useState([]);
  const [categoryPosts, setCategoryPosts] = useState([]);
  const [loadingCategory, setLoadingCategory] = useState(false);

  useEffect(() => {
    // Categories, creators and sponsors load independently so the filter bar
    // always renders even if the personalized /feed call fails (e.g. session issue).
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => setCategories([]));
    api.get("/creators").then(({ data }) => setCreators(data)).catch(() => setCreators([]));
    api.get("/sponsorships").then(({ data }) => setSponsors(data)).catch(() => setSponsors([]));
    api.get("/sponsored-posts").then(({ data }) => setSponsoredPosts(data)).catch(() => setSponsoredPosts([]));
    api.get("/fan/badges/me").then(({ data }) => setFanBadges(data)).catch(() => setFanBadges([]));

    (async () => {
      try {
        const feedRes = await api.get("/feed");
        let feedPosts = feedRes.data;
        if (!feedPosts.length) {
          // Not following anyone yet — show discovery posts from all creators
          const creatorsRes = await api.get("/creators");
          const all = await Promise.all(
            creatorsRes.data.slice(0, 6).map((c) => api.get(`/creators/${c.id}`))
          );
          feedPosts = all.flatMap((r) => r.data.posts);
        }
        setPosts(feedPosts);
      } catch (e) {
        setPosts([]);
      }
    })();
  }, []);

  // When a specific category is selected, fetch posts from ALL creators in that
  // category (discovery), not just the ones the fan already follows.
  useEffect(() => {
    if (category === "Tutte") { setCategoryPosts([]); return; }
    let cancelled = false;
    (async () => {
      setLoadingCategory(true);
      try {
        const { data } = await api.get(`/feed?category=${encodeURIComponent(category)}`);
        if (!cancelled) setCategoryPosts(data);
      } catch (e) {
        if (!cancelled) setCategoryPosts([]);
      } finally {
        if (!cancelled) setLoadingCategory(false);
      }
    })();
    return () => { cancelled = true; };
  }, [category]);

  if (user && user.role === "admin") return <Navigate to="/admin" replace />;

  const filteredCreators = creators.filter((c) =>
    c.display_name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const displayPosts = category === "Tutte" ? (posts || []) : categoryPosts;

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen">
      <AppNav />
      <main className="flex-1 flex justify-center gap-8 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="w-full max-w-2xl">
          <h1 className="text-3xl font-black font-heading mb-1">Il tuo feed</h1>
          <p className="text-white/50 mb-5 text-sm">Contenuti dai creator che segui e scoperte per te.</p>

          {/* Mobile creator search */}
          <div className="xl:hidden mb-6" data-testid="mobile-search-block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="creator-search-mobile"
                placeholder="Cerca creator per nome…"
                className="pl-9 bg-[#1A1A1A] border-white/10 h-11 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
              />
            </div>
            {search.trim() && (
              <div className="mt-3 bg-[#1A1A1A] border border-white/10 rounded-2xl p-2" data-testid="mobile-search-results">
                {filteredCreators.length === 0 ? (
                  <p className="text-sm text-white/40 py-4 text-center">Nessun creator trovato.</p>
                ) : (
                  filteredCreators.slice(0, 6).map((c) => (
                    <Link
                      key={c.id} to={`/creator/${c.id}`} data-testid={`discover-mobile-${c.id}`}
                      className="flex items-center gap-3 p-2 rounded-xl active:scale-[0.98] transition-transform"
                    >
                      <img src={mediaUrl(c.avatar_url)} alt={c.display_name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-semibold truncate">{c.display_name}</span>
                          {c.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#F5C6D6] shrink-0" />}
                        </div>
                        <p className="text-xs text-white/50">{formatCount(c.follower_count)} follower</p>
                      </div>
                      {c.profile_type === "subscription" && (
                        <span className="text-xs text-[#E91E8C] font-semibold">€{Number(c.subscription_price).toFixed(2)}</span>
                      )}
                    </Link>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Fan recognition showcase */}
          {user?.role === "fan" && fanBadges.length > 0 && (
            <div className="mb-6" data-testid="fan-recognition-strip">
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-4 h-4 text-[#F5C6D6]" />
                <h2 className="text-xs font-bold font-heading uppercase tracking-wider text-white/70">I tuoi riconoscimenti</h2>
              </div>
              <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
                {fanBadges.map((b) => (
                  <Link key={b.creator_id} to={`/creator/${b.creator_id}`} data-testid={`fan-recognition-${b.creator_id}`} className="flex items-center gap-2 bg-[#1A1A1A] border border-white/10 rounded-full pl-1.5 pr-3 py-1.5 shrink-0 hover:border-[#E91E8C]/40 transition-colors">
                    <img src={mediaUrl(b.avatar_url)} alt={b.display_name} className="w-6 h-6 rounded-full object-cover" />
                    <span className="text-xs font-semibold whitespace-nowrap">{b.display_name}</span>
                    <SupporterBadge badge={b.badge} size="xs" />
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Category filter chips */}
          {categories.length > 0 && (
            <CategoryChips categories={categories} category={category} setCategory={setCategory} />
          )}

          {/* Sponsored creators */}
          {category === "Tutte" && sponsors.length > 0 && (
            <div className="mb-8" data-testid="sponsored-section">
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="w-4 h-4 text-[#F5C6D6]" />
                <h2 className="text-sm font-bold font-heading uppercase tracking-wider text-[#F5C6D6]">Sponsorizzato</h2>
              </div>
              <div className="flex gap-3 overflow-x-auto -mx-5 sm:-mx-8 px-5 sm:px-8 pb-1">
                {sponsors.map((s) => (
                  <Link
                    key={s.creator_id}
                    to={`/creator/${s.creator_id}`}
                    data-testid={`sponsored-${s.creator_id}`}
                    className="shrink-0 w-40 bg-[#1A1A1A] border border-[#F5C6D6]/30 rounded-2xl p-4 card-hover-glow"
                  >
                    <div className="relative w-16 h-16 mx-auto mb-3">
                      <img src={mediaUrl(s.avatar_url)} alt={s.display_name} className="w-16 h-16 rounded-full object-cover border border-white/10" />
                      <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px] font-bold uppercase tracking-wider bg-[#F5C6D6] text-black px-2 py-0.5 rounded-full">ADV</span>
                    </div>
                    <div className="flex items-center justify-center gap-1 mb-0.5">
                      <span className="text-sm font-semibold truncate">{s.display_name}</span>
                      {s.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#F5C6D6] shrink-0" />}
                    </div>
                    <p className="text-xs text-center text-[#E91E8C] font-semibold">
                      {s.profile_type === "subscription" ? `€${Number(s.subscription_price).toFixed(2)}/mese` : "Profilo gratuito"}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Post in evidenza (sponsored posts) */}
          {category === "Tutte" && sponsoredPosts.length > 0 && (
            <div className="mb-8" data-testid="sponsored-posts-section">
              <div className="flex items-center gap-2 mb-3">
                <Megaphone className="w-4 h-4 text-[#F5C6D6]" />
                <h2 className="text-sm font-bold font-heading uppercase tracking-wider text-[#F5C6D6]">Post in evidenza</h2>
              </div>
              <div className="space-y-6">
                {sponsoredPosts.slice(0, 3).map((p) => (
                  <PostCard key={`spost-${p.id}`} post={{ ...p, sponsored: true }} />
                ))}
              </div>
            </div>
          )}

          {posts === null || loadingCategory ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>
          ) : displayPosts.length === 0 ? (
            <div className="text-center py-20 text-white/50" data-testid="feed-empty">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-[#E91E8C]" />
              {category === "Tutte"
                ? "Nessun contenuto ancora. Esplora i creator per iniziare."
                : `Nessun contenuto nella categoria "${category}".`}
            </div>
          ) : (
            <div className="space-y-6" data-testid="feed-list">
              {displayPosts.map((p, idx) => {
                const nodes = [<PostCard key={p.id} post={p} />];
                if (category === "Tutte" && sponsoredPosts.length && (idx + 1) % 4 === 0 && idx + 1 < displayPosts.length) {
                  const sp = sponsoredPosts[(Math.floor(idx / 4)) % sponsoredPosts.length];
                  nodes.push(<PostCard key={`inline-spost-${idx}-${sp.id}`} post={{ ...sp, sponsored: true }} />);
                }
                if (sponsors.length && (idx + 1) % 5 === 0 && idx + 1 < displayPosts.length) {
                  const sp = sponsors[(Math.floor(idx / 5)) % sponsors.length];
                  nodes.push(<SponsoredFeedCard key={`spon-${idx}-${sp.creator_id}`} sponsor={sp} />);
                }
                return nodes;
              })}
            </div>
          )}
        </div>

        <aside className="hidden xl:block w-80 shrink-0">
          <div className="sticky top-8 space-y-4">
            {/* Ricerca creator */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-5">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="creator-search"
                  placeholder="Cerca creator per nome…"
                  className="pl-9 bg-[#0D0D0D] border-white/10 h-11 rounded-xl focus:border-[#E91E8C] focus-visible:ring-[#E91E8C]"
                />
              </div>
            </div>

            {/* Creator list */}
            <div className="bg-[#1A1A1A] border border-white/10 rounded-3xl p-6">
              <div className="flex items-center gap-2 mb-5">
                <Compass className="w-5 h-5 text-[#E91E8C]" />
                <h3 className="font-bold font-heading">{search ? "Risultati" : "Creator in evidenza"}</h3>
              </div>
              <div className="space-y-3" data-testid="creator-results">
                {filteredCreators.length === 0 ? (
                  <p className="text-sm text-white/40 py-4 text-center" data-testid="creator-no-results">Nessun creator trovato.</p>
                ) : (
                  filteredCreators.slice(0, 8).map((c) => (
                    <Link
                      key={c.id} to={`/creator/${c.id}`} data-testid={`discover-${c.id}`}
                      className="flex items-center gap-3 p-2 rounded-xl card-hover-glow border border-transparent"
                    >
                      <img src={mediaUrl(c.avatar_url)} alt={c.display_name} className="w-11 h-11 rounded-full object-cover border border-white/10" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-semibold truncate">{c.display_name}</span>
                          {c.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-[#F5C6D6] shrink-0" />}
                        </div>
                        <p className="text-xs text-white/50">{formatCount(c.follower_count)} follower</p>
                      </div>
                      {c.profile_type === "subscription" && (
                        <span className="ml-auto text-xs text-[#E91E8C] font-semibold">€{Number(c.subscription_price).toFixed(2)}</span>
                      )}
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}
